import React, { useMemo, useState } from 'react';
import {
  CHANNELS, AUDIENCES, ATTENDANCE_NOTICE_WORDING,
  CANCELLATION_NOTE_MAX, cancellationRecipients,
} from '../../../domain/notifications';
import { useSmsAvailable } from '../../../context/SmsGateContext';
import SmsUnavailable, { smsOffClass } from '../../SmsUnavailable';
import useNames from '../../../hooks/useNames';
import { noticeChannelBox } from '../../../domain/lessonAttendance';
import s from './cancellationNotices.module.css';

// "You have called this lesson off — do you want to tell the family?" (#187)
//
// Marking a lesson Teacher Cancelled is the way to call it off without
// deleting it (#152), so it offers the same notice the Delete Event dialog
// (RecurringScopeDialog.jsx) does. Whether MMS offers a notice on this surface,
// and how, is unchecked (#187); this is not claimed as parity.
//
//   * Offered, never automatic, and unticked: a message leaving the building
//     is a thing to decide. The reminder-already-sent line below informs that
//     choice rather than making it.
//   * The same controls and labels as the delete dialog, which are MMS's
//     ("Email students", "SMS students"), because it is the same gesture. The
//     plural stays although an attendance row is one student.
//
// Not the same component as the delete dialog's: that copy is MMS-verbatim for
// a delete — its warning ends "you can still delete the event", false here.
// What the two must agree on is who "students" means and what counts as a
// reachable address, and that is one function, `cancellationRecipients`, called
// by both, so the box and the messages queued cannot disagree.

// MMS's wording with its tail rewritten, since there is no delete here: it
// still says nothing is blocked — an unreachable family does not stop a
// teacher marking a lesson off. The words are `ATTENDANCE_NOTICE_WORDING` in
// domain/notifications.js.
const NOTICE_CHANNELS = [
  { channel: CHANNELS.email, ...ATTENDANCE_NOTICE_WORDING.email },
  { channel: CHANNELS.sms, ...ATTENDANCE_NOTICE_WORDING.sms },
];

// A checkbox whose third state is a DOM property rather than an attribute, so
// React cannot set it from JSX and it needs a ref.
function TriStateCheckbox({ checked, indeterminate, onChange, label, disabled = false }) {
  return (
    <label className={disabled ? `${s.choice} ${smsOffClass}` : s.choice}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        ref={(el) => { if (el) el.indeterminate = Boolean(indeterminate && !checked); }}
        onChange={onChange}
      />
      <span>{label}</span>
    </label>
  );
}

export default function CancellationNotices({
  notices, groups = [], onToggleChannel, onSetAudience,
  note, onNoteChange, reminderWarning = null, footer = null,
}) {
  const names = useNames();
  const [dismissed, setDismissed] = useState({});
  // Whether the SMS channel can be taken at all (#782, domain/smsGate.js).
  // useAttendanceForm drops the SMS half of the send on the same answer.
  const smsAvailable = useSmsAvailable();

  // Who would be written to and who cannot be reached, per channel, from the
  // same domain function that resolves the addresses the notices are queued
  // to, so this box and the send cannot disagree.
  const resolved = useMemo(() => Object.fromEntries(NOTICE_CHANNELS.map(({ channel }) => {
    const chosen = notices[channel];
    if (!chosen?.open) return [channel, { notices: [], unreachable: [] }];
    return [channel, cancellationRecipients(groups, {
      channel, audience: chosen, nameFormats: names.formats,
    })];
  })), [notices, groups, names]);

  return (
    <div className={s.block}>
      {/* What the family is holding, above the ticks: it informs the decision,
          and a teacher reads downwards into the controls. */}
      {reminderWarning ? (
        <div className={s.reminded} role="status">{reminderWarning}</div>
      ) : null}

      {NOTICE_CHANNELS.map((c) => {
        // The SMS channel while texts cannot be sent (#782): disabled, drawn
        // closed whatever the form holds, with its one line under it.
        const gated = c.channel === CHANNELS.sms && !smsAvailable;
        const chosen = gated ? {} : (notices[c.channel] || {});
        const missing = resolved[c.channel]?.unreachable || [];
        const box = noticeChannelBox(chosen);
        return (
          <div key={c.channel} className={s.channel}>
            <TriStateCheckbox
              label={c.label}
              checked={box.checked}
              indeterminate={box.indeterminate}
              disabled={gated}
              onChange={() => onToggleChannel(c.channel)}
            />
            {gated ? <SmsUnavailable /> : null}

            {chosen.open ? (
              <div className={s.audience}>
                {[[AUDIENCES.students, c.students], [AUDIENCES.parents, c.parents]].map(([key, label]) => (
                  <label key={key} className={s.choice}>
                    <input
                      type="checkbox"
                      checked={Boolean(chosen[key])}
                      onChange={(e) => onSetAudience(c.channel, { [key]: e.target.checked })}
                    />
                    <span>{label}</span>
                  </label>
                ))}

                {missing.length && !dismissed[c.channel] ? (
                  <div className={s.warning} role="status">
                    <button
                      type="button"
                      className={s.dismiss}
                      aria-label="Dismiss"
                      onClick={() => setDismissed((d) => ({ ...d, [c.channel]: true }))}
                    >
                      ×
                    </button>
                    <span className={s.warningText}>{c.warning}</span>
                    {missing.map((r) => (
                      <span key={r.key} className={s.missing}>{r.listName}</span>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}

      {/* One message for both channels — MMS's single Cancellation message,
          the %Note% its Event Cancelled template interpolates. It is also the
          only answer to what a family already reminded should be told: a
          separate retraction template would be family-facing copy with no MMS
          to check it against (#187). */}
      <div className={s.messageField}>
        <span className={s.messageLabel} id="attendance-cancellation-message">Cancellation message</span>
        <textarea
          className={s.messageInput}
          aria-labelledby="attendance-cancellation-message"
          rows={3}
          maxLength={CANCELLATION_NOTE_MAX}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
        />
        <span className={s.hint}>Optional</span>
      </div>

      {/* Send Cancellation Notice (#732), handed in by AttendanceTab: the
          notice goes from this button, not from Save — a knowing difference
          from MMS, whose Save sends — and the button saves the mark first. */}
      {footer}
    </div>
  );
}
